import { normalizeLesson } from "../../LessonPlanner/utils/lessonPlanUtils";
import { APPROVAL_STATUS, WEEKLY_UPDATE_STATUS } from "../WeeklyDiaryConstant";

const num = (value) => Number(value) || 0;

export const getInitials = (name = "") =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("");

const SENT_STATUSES = ["submitted", "pending", "approved", "needs_attention"];

/** Weekly Update chip: explicit field from the API, else derived from the diary status. */
export const getWeeklyUpdateStatus = (row) =>
  WEEKLY_UPDATE_STATUS[row.weeklyUpdateStatus] ||
  (SENT_STATUSES.includes(row.status) ? WEEKLY_UPDATE_STATUS.sent : WEEKLY_UPDATE_STATUS.in_progress);

/** Principal's Approval chip; null while the teacher hasn't sent the diary. */
export const getApprovalStatus = (row) => {
  if (row.approvalStatus) return APPROVAL_STATUS[row.approvalStatus] || null;
  if (row.status === "approved") return APPROVAL_STATUS.approved;
  if (row.status === "needs_attention") return APPROVAL_STATUS.needs_attention;
  if (SENT_STATUSES.includes(row.status)) return APPROVAL_STATUS.pending;
  return null;
};

/**
 * One overview row (teacher + subject). The week is sent once for the whole list,
 * so it is passed in from the payload (a row's own week, if ever sent, wins).
 */
const normalizeOverviewRow = (raw = {}, week = {}) => ({
  teacherId: raw.teacher_id ?? raw.teacher?.id ?? "",
  teacherName: raw.teacher_name ?? raw.teacher?.name ?? "",
  teacherAvatar: raw.teacher_avatar ?? raw.teacher?.profile_image ?? raw.teacher?.avatar ?? "",
  subjectId: raw.subject_id ?? raw.subject?.id ?? "",
  subjectName: raw.subject_name ?? raw.subject?.name ?? "",
  total: num(raw.total ?? raw.total_lessons),
  completed: num(raw.completed),
  flagged: num(raw.flagged),
  pending: num(raw.pending),
  due: num(raw.due),
  completionPercent: num(raw.completion_percent),
  diaryId: raw.diary_id ?? null,
  status: raw.diary_status || raw.status || "not_submitted",
  // Backend's own wording for the status, e.g. "Not Submitted"
  statusLabel: raw.diary_status_label || "",
  weeklyUpdateStatus: raw.weekly_update_status || "",
  approvalStatus: raw.approval_status || raw.principal_approval || "",
  weekStart: raw.week_start ?? week.weekStart ?? "",
  weekEnd: raw.week_end ?? week.weekEnd ?? "",
  weekLabel: raw.week_label ?? week.weekLabel ?? "",
});

/**
 * GET lesson-plan/diary/overview
 * Response: { week_start, week_end, week_label, summary: { total_lesson_plans, diaries_expected,
 *   diaries_submitted, diaries_submitted_percent, diaries_approved, with_overdue, avg_completion_percent },
 *   rows: [{ teacher, subject, total, completed, flagged, pending, due, completion_percent,
 *   diary_id, diary_status, diary_status_label }] }
 */
export const normalizeDiaryOverview = (response) => {
  const payload = response?.data?.data ?? {};
  const list = Array.isArray(payload) ? payload : payload.rows ?? payload.teachers ?? payload.data ?? [];
  const summary = Array.isArray(payload) ? {} : payload.summary || {};
  const week = {
    weekStart: payload.week_start || "",
    weekEnd: payload.week_end || "",
    weekLabel: payload.week_label || "",
  };

  return {
    ...week,
    rows: list.map((row) => normalizeOverviewRow(row, week)),
    summary: {
      totalLessonPlans: num(summary.total_lesson_plans),
      diariesExpected: num(summary.diaries_expected),
      diariesSubmitted: num(summary.diaries_submitted),
      diariesApproved: num(summary.diaries_approved),
      submittedPercentage: num(summary.diaries_submitted_percent ?? summary.submitted_percentage),
      overdue: num(summary.with_overdue ?? summary.overdue),
      avgCompletion: num(summary.avg_completion_percent ?? summary.avg_completion),
    },
  };
};

const countBy = (lessons, predicate) => lessons.filter(predicate).length;

/**
 * GET lesson-plan/diary -> one teacher's diary for a subject and week.
 * Response: { teacher, subject, week_start, week_end, summary, lessons[], diary: { id, status, reflection }, can_approve }
 */
export const normalizeDiary = (response) => {
  const payload = response?.data?.data ?? {};
  const rawLessons = Array.isArray(payload) ? payload : payload.lessons ?? payload.data ?? [];
  // Lessons don't repeat the subject; they all belong to the diary's subject
  const diarySubject = Array.isArray(payload) ? {} : payload.subject || {};
  const lessons = rawLessons
    .map((raw) => normalizeLesson({ subject: diarySubject, ...raw }))
    .filter((lesson) => lesson.hasPlan)
    .sort((a, b) => `${a.lessonDate} ${a.startTime}`.localeCompare(`${b.lessonDate} ${b.startTime}`));
  const summary = Array.isArray(payload) ? {} : payload.summary || {};
  const diary = Array.isArray(payload) ? {} : payload.diary || payload;
  // Approver is sent as `approved_by_user: { id, name }` (`approved_by` is just their id)
  const approver =
    diary.approved_by_user ||
    (diary.approved_by && typeof diary.approved_by === "object" ? diary.approved_by : {});
  const isApproved = Boolean(diary.is_approved ?? diary.approved_at ?? diary.status === "approved");
  // Same "sent" statuses as the overview list, so both pages agree on whether it can be approved
  const isSubmitted = Boolean(diary.is_submitted ?? diary.submitted_at ?? SENT_STATUSES.includes(diary.status));

  return {
    teacherId: diary.teacher_id ?? payload.teacher?.id ?? "",
    teacherName: diary.teacher_name ?? payload.teacher?.name ?? "",
    subjectId: diary.subject_id ?? payload.subject?.id ?? "",
    subjectName: diary.subject_name ?? payload.subject?.name ?? "",
    status: diary.status || "not_submitted",
    reflection: diary.reflection || "",
    approvalRemarks: diary.approver_remarks ?? diary.approval_remarks ?? diary.remarks ?? "",
    // Who approved and when, for the "Principal's Remarks" popup
    approvedByName: approver.name ?? diary.approved_by_name ?? "",
    approvedByRole: approver.role_name ?? approver.role ?? diary.approved_by_role ?? "",
    approvedByAvatar: approver.profile_image ?? approver.avatar ?? diary.approved_by_avatar ?? "",
    approvedAt: diary.approved_at || "",
    isSubmitted,
    isApproved,
    // The backend decides who may approve (and when); fall back to "submitted, not yet approved"
    canApprove: Boolean(payload.can_approve ?? (isSubmitted && !isApproved)),
    weekStart: payload.week_start || "",
    weekEnd: payload.week_end || "",
    lessons,
    summary: {
      total: num(summary.total ?? lessons.length),
      completed: num(summary.completed ?? countBy(lessons, (l) => l.status === "completed")),
      pending: num(summary.pending ?? countBy(lessons, (l) => l.status === "pending")),
      flagged: num(summary.flagged ?? countBy(lessons, (l) => l.isFlagged)),
      due: num(summary.due),
    },
  };
};
